import React, { useState, useEffect } from 'react';
import { Card, Radio, Button, Space, Tag, Divider, Spin, Typography, InputNumber, Table, Modal, Switch, Input, Select, DatePicker, Checkbox, message, Tooltip, Badge, Empty, Popconfirm, Collapse } from 'antd';
import { RobotOutlined, ThunderboltOutlined, SettingOutlined, HistoryOutlined, PlayCircleOutlined, ReloadOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, EditOutlined, DeleteOutlined, PlusOutlined, BulbOutlined, SoundOutlined, StopOutlined, AimOutlined, FileTextOutlined, ToolOutlined, EyeOutlined, EyeInvisibleOutlined, GlobalOutlined, BookOutlined, RocketOutlined, ShoppingOutlined, MessageOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;
const API_BASE = '/api/v1';

const PLATFORM_CONFIG = {
  ALL:       { label: '전체',      color: '#1890ff', emoji: '🌐' },
  REAL:      { label: 'REAL',      color: '#722ed1', emoji: '📚' },
  TRADESAGA: { label: 'TradeSaga', color: '#f5222d', emoji: '📈' },
  WORKON:    { label: 'WorkOn',    color: '#13c2c2', emoji: '💼' },
};

const PLATFORM_KEYS = ['REAL', 'TRADESAGA', 'WORKON'];

const USER_TYPE_CONFIG = {
  power_user: { label: '🔥 파워유저', color: '#f5222d', bg: '#fff1f0' },
  normal:     { label: '👤 일반', color: '#1890ff', bg: '#e6f7ff' },
  at_risk:    { label: '⚠️ 이탈위험', color: '#fa8c16', bg: '#fff7e6' },
  new_user:   { label: '🆕 신규', color: '#52c41a', bg: '#f6ffed' },
  dormant:    { label: '💤 휴면', color: '#8c8c8c', bg: '#fafafa' },
};

const MODE_INFO = {
  full_auto: {
    title: 'A) 완전 자동', icon: '🚀',
    desc: 'AI가 분석 → 캠페인 생성 → 이메일 발송까지 전부 자동',
    detail: '모든 유형의 사용자에게 AI가 판단한 최적의 마케팅 액션을 자동 실행합니다.',
    color: '#f5222d', badge: 'red',
  },
  semi_auto: {
    title: 'B) 반자동', icon: '🔧',
    desc: '분석은 자동, 발송은 관리자 확인 후',
    detail: 'AI가 사용자를 분석하고 추천 액션을 생성하지만, 캠페인 발송은 관리자가 직접 승인합니다.',
    color: '#1890ff', badge: 'blue',
  },
  conditional_auto: {
    title: 'C) 조건부 자동', icon: '⚡',
    desc: '이탈위험/휴면에게만 자동 발송',
    detail: '이탈 방지가 급한 사용자에게만 AI가 자동으로 리텐션 캠페인을 발송합니다.',
    color: '#fa8c16', badge: 'orange',
  },
};

const DIRECTIVE_TYPES = {
  emphasis: { label: '📢 강조 메시지', color: '#f5222d', icon: <SoundOutlined />, desc: '이번 주기에 특별히 강조할 메시지나 테마' },
  tone:     { label: '🎨 톤/스타일', color: '#722ed1', icon: <BulbOutlined />, desc: '이메일의 분위기와 어조 지시' },
  exclusion:{ label: '🚫 제외 사항', color: '#fa541c', icon: <StopOutlined />, desc: '언급하지 말아야 할 내용이나 제외할 대상' },
  cta:      { label: '🎯 CTA 방향', color: '#13c2c2', icon: <AimOutlined />, desc: '사용자에게 유도할 행동 방향' },
  context:  { label: '📋 배경 정보', color: '#1890ff', icon: <FileTextOutlined />, desc: '현재 상황이나 맥락 정보' },
  custom:   { label: '💡 기타', color: '#8c8c8c', icon: <ToolOutlined />, desc: '기타 지시사항' },
};

const AutoCampaign = () => {
  // 플랫폼 선택
  const [selectedPlatform, setSelectedPlatform] = useState('REAL');

  // 플랫폼별 설정 캐시
  const [platformSettings, setPlatformSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 현재 편집 중인 설정
  const [mode, setMode] = useState('semi_auto');
  const [intervalDays, setIntervalDays] = useState(14);
  const [isEnabled, setIsEnabled] = useState(true);

  // 실행/요약
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  const [detailModal, setDetailModal] = useState(null);

  // 지시사항
  const [directives, setDirectives] = useState([]);
  const [directivesLoading, setDirectivesLoading] = useState(false);
  const [showDirectiveModal, setShowDirectiveModal] = useState(false);
  const [editingDirective, setEditingDirective] = useState(null);
  const [directiveForm, setDirectiveForm] = useState({
    directive_type: 'emphasis', title: '', content: '',
    target_user_types: null, priority: 0, valid_from: null, valid_until: null, is_active: true,
  });

  // 탭
  const [activeTab, setActiveTab] = useState('directives');

  useEffect(() => {
    loadAllPlatformSettings();
    loadRuns();
    loadDirectives();
  }, []);

  useEffect(() => {
    loadSummary(selectedPlatform);
    // Apply cached settings for this platform
    const cached = platformSettings[selectedPlatform];
    if (cached) {
      setMode(cached.mode || 'semi_auto');
      setIntervalDays(cached.interval_days || 14);
      setIsEnabled(cached.is_enabled !== false);
    }
  }, [selectedPlatform, platformSettings]);

  // ── 모든 플랫폼 설정 로드 ──
  const loadAllPlatformSettings = async () => {
    setSettingsLoading(true);
    const allSettings = {};
    for (const platform of PLATFORM_KEYS) {
      try {
        const res = await fetch(`${API_BASE}/insights/agent/settings?platform=${platform}`);
        if (res.ok) allSettings[platform] = await res.json();
      } catch { /* default */ }
    }
    setPlatformSettings(allSettings);
    // Set current platform's settings
    const current = allSettings[selectedPlatform] || {};
    setMode(current.mode || 'semi_auto');
    setIntervalDays(current.interval_days || 14);
    setIsEnabled(current.is_enabled !== false);
    setSettingsLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/insights/agent/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, interval_days: intervalDays, is_enabled: isEnabled, platform: selectedPlatform }),
      });
      if (res.ok) {
        message.success(`${PLATFORM_CONFIG[selectedPlatform].label} 설정 저장!`);
        // Update cache
        setPlatformSettings(prev => ({
          ...prev,
          [selectedPlatform]: { mode, interval_days: intervalDays, is_enabled: isEnabled, platform: selectedPlatform },
        }));
      } else { message.error('저장 실패'); }
    } catch { message.error('저장 중 오류'); }
    setSaving(false);
  };

  const loadRuns = async () => {
    setRunsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/insights/batch/runs`);
      if (res.ok) setRuns(await res.json());
    } catch { /* ignore */ }
    setRunsLoading(false);
  };

  const loadSummary = async (platform) => {
    try {
      const param = platform && platform !== 'ALL' ? `?platform=${platform}` : '';
      const res = await fetch(`${API_BASE}/insights/batch/summary${param}`);
      if (res.ok) setSummary(await res.json());
    } catch { /* ignore */ }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      const res = await fetch(`${API_BASE}/insights/batch/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ run_type: 'manual', platform: selectedPlatform }),
      });
      if (res.ok) {
        message.success(`${PLATFORM_CONFIG[selectedPlatform].label} AI 에이전트 실행 시작!`);
        setTimeout(() => { loadRuns(); loadSummary(selectedPlatform); }, 5000);
      }
    } catch { message.error('실행 실패'); }
    setRunningNow(false);
  };

  const loadRunDetail = async (runId) => {
    try {
      const res = await fetch(`${API_BASE}/insights/batch/run/${runId}`);
      if (res.ok) setDetailModal(await res.json());
    } catch { message.error('상세 로딩 실패'); }
  };

  // ── 지시사항 ──
  const loadDirectives = async () => {
    setDirectivesLoading(true);
    try {
      const res = await fetch(`${API_BASE}/insights/directives`);
      if (res.ok) setDirectives(await res.json());
    } catch { /* ignore */ }
    setDirectivesLoading(false);
  };

  const resetDirectiveForm = () => {
    setDirectiveForm({ directive_type: 'emphasis', title: '', content: '', target_user_types: null, priority: 0, valid_from: null, valid_until: null, is_active: true });
    setEditingDirective(null);
  };

  const openCreateModal = (type = null) => {
    resetDirectiveForm();
    if (type) setDirectiveForm(f => ({ ...f, directive_type: type }));
    setShowDirectiveModal(true);
  };

  const openEditModal = (directive) => {
    setEditingDirective(directive);
    setDirectiveForm({
      directive_type: directive.directive_type, title: directive.title, content: directive.content,
      target_user_types: directive.target_user_types, priority: directive.priority,
      valid_from: directive.valid_from, valid_until: directive.valid_until, is_active: directive.is_active,
    });
    setShowDirectiveModal(true);
  };

  const saveDirective = async () => {
    if (!directiveForm.title.trim() || !directiveForm.content.trim()) { message.warning('제목과 내용을 입력해주세요.'); return; }
    try {
      const url = editingDirective ? `${API_BASE}/insights/directives/${editingDirective.id}` : `${API_BASE}/insights/directives`;
      const method = editingDirective ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(directiveForm) });
      if (res.ok) { message.success(editingDirective ? '수정됨' : '추가됨'); setShowDirectiveModal(false); resetDirectiveForm(); loadDirectives(); }
      else { message.error('저장 실패'); }
    } catch { message.error('오류 발생'); }
  };

  const toggleDirective = async (id) => {
    try { const res = await fetch(`${API_BASE}/insights/directives/${id}/toggle`, { method: 'PUT' }); if (res.ok) loadDirectives(); }
    catch { message.error('토글 실패'); }
  };

  const deleteDirective = async (id) => {
    try { const res = await fetch(`${API_BASE}/insights/directives/${id}`, { method: 'DELETE' }); if (res.ok) { message.success('삭제됨'); loadDirectives(); } }
    catch { message.error('삭제 실패'); }
  };

  // ── 실행 이력 (플랫폼 필터) ──
  const filteredRuns = runs.filter(r => {
    if (!r.platform) return true; // 레거시
    return r.platform === selectedPlatform || r.platform === selectedPlatform.toLowerCase();
  });

  const runColumns = [
    { title: '상태', dataIndex: 'status', width: 80,
      render: (s) => <Tag color={s === 'completed' ? 'green' : s === 'running' ? 'blue' : 'red'}>{s === 'completed' ? '완료' : s === 'running' ? '실행중' : '실패'}</Tag> },
    { title: '플랫폼', dataIndex: 'platform', width: 100,
      render: (p) => { const cfg = PLATFORM_CONFIG[p] || PLATFORM_CONFIG[(p||'').toUpperCase()]; return cfg ? <Tag color={cfg.color}>{cfg.emoji} {cfg.label}</Tag> : <Tag>{p || '-'}</Tag>; } },
    { title: '유형', dataIndex: 'run_type', width: 70, render: (t) => <Tag>{t === 'scheduled' ? '자동' : '수동'}</Tag> },
    { title: '분석', dataIndex: 'total_analyzed', width: 60, render: (v) => `${v || 0}명` },
    { title: '🔥', dataIndex: 'power_users', width: 40 },
    { title: '⚠️', dataIndex: 'at_risk', width: 40 },
    { title: '액션', dataIndex: 'actions_created', width: 60, render: (v) => `${v || 0}건` },
    { title: '시간', dataIndex: 'started_at', width: 130,
      render: (v) => { if (!v) return '-'; const d = new Date(v + 'Z'); return d.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } },
    { title: '', width: 50, render: (_, r) => <Button size="small" type="link" onClick={() => loadRunDetail(r.id)}>상세</Button> },
  ];

  const activeDirectivesCount = directives.filter(d => d.is_active).length;
  const currentConfig = PLATFORM_CONFIG[selectedPlatform] || PLATFORM_CONFIG.REAL;

  if (settingsLoading) return <div style={{ textAlign: 'center', padding: 100 }}><Spin size="large" /></div>;

  return (
    <div>
      {/* ── 헤더 ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}><RobotOutlined style={{ marginRight: 8 }} />AI 자동 캠페인</Title>
          <Text type="secondary">플랫폼별로 AI 에이전트 설정 및 마케팅 액션을 관리합니다.</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => { loadRuns(); loadSummary(selectedPlatform); loadDirectives(); loadAllPlatformSettings(); }}>새로고침</Button>
          <Button type="primary" icon={<PlayCircleOutlined />} loading={runningNow} onClick={handleRunNow}>
            {currentConfig.emoji} {currentConfig.label} 실행
          </Button>
        </Space>
      </div>

      {/* ── 플랫폼 선택 (카드형) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${PLATFORM_KEYS.length}, 1fr)`, gap: 12, marginBottom: 24 }}>
        {PLATFORM_KEYS.map(key => {
          const config = PLATFORM_CONFIG[key];
          const settings = platformSettings[key] || {};
          const isSelected = selectedPlatform === key;
          const modeInfo = MODE_INFO[settings.mode] || MODE_INFO.semi_auto;
          return (
            <div
              key={key}
              onClick={() => setSelectedPlatform(key)}
              style={{
                border: isSelected ? `2px solid ${config.color}` : '1px solid #e8e8e8',
                borderRadius: 12, padding: '16px 18px', cursor: 'pointer',
                background: isSelected ? `${config.color}06` : '#fff',
                transition: 'all 0.2s',
                position: 'relative',
              }}
            >
              {/* 활성 상태 뱃지 */}
              <div style={{ position: 'absolute', top: 10, right: 10 }}>
                <Badge status={settings.is_enabled !== false ? 'success' : 'default'} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>{config.emoji}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: isSelected ? config.color : '#333' }}>{config.label}</div>
                  <div style={{ fontSize: 11, color: '#999' }}>
                    {settings.is_enabled !== false ? '활성' : '비활성'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Tag color={modeInfo.badge} style={{ fontSize: 10, margin: 0 }}>
                  {modeInfo.icon} {settings.mode === 'full_auto' ? '완전자동' : settings.mode === 'conditional_auto' ? '조건부' : '반자동'}
                </Tag>
                <Tag style={{ fontSize: 10, margin: 0 }}>{settings.interval_days || 14}일 주기</Tag>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 사용자 분포 요약 (선택된 플랫폼) ── */}
      {summary && summary.total_analyzed > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          {Object.entries(USER_TYPE_CONFIG).map(([key, config]) => {
            const data = summary.user_type_distribution?.[key];
            return (
              <Card key={key} size="small" style={{ textAlign: 'center', borderTop: `3px solid ${config.color}` }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: config.color }}>{data?.count || 0}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{config.label}</div>
                {data?.avg_score && <div style={{ fontSize: 11, color: '#999' }}>평균 {data.avg_score}점</div>}
              </Card>
            );
          })}
        </div>
      )}

      {/* ── 탭 네비게이션 ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #f0f0f0', paddingBottom: 0 }}>
        {[
          { key: 'directives', label: '🧑‍💼 마케터 지시사항', badge: activeDirectivesCount },
          { key: 'settings', label: `⚙️ ${currentConfig.label} 설정`, badge: null },
          { key: 'history', label: '📜 실행 이력', badge: filteredRuns.length },
        ].map(tab => (
          <div key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px', cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? currentConfig.color : '#666',
              borderBottom: activeTab === tab.key ? `2px solid ${currentConfig.color}` : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 6, fontSize: 14,
            }}>
            {tab.label}
            {tab.badge > 0 && (
              <span style={{
                background: activeTab === tab.key ? currentConfig.color : '#d9d9d9',
                color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600,
              }}>{tab.badge}</span>
            )}
          </div>
        ))}
      </div>

      {/* ════════════════════════════════════════ */}
      {/* 탭 1: 마케터 지시사항 */}
      {/* ════════════════════════════════════════ */}
      {activeTab === 'directives' && (
        <div>
          <div style={{
            background: 'linear-gradient(135deg, #e6f4ff 0%, #f0f5ff 100%)',
            border: '1px solid #91caff', borderRadius: 10, padding: '16px 20px',
            marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 28 }}>🧑‍💼</span>
            <div>
              <Text strong style={{ fontSize: 14 }}>마케터 지시사항</Text><br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                여기서 작성한 지시사항은 AI 에이전트가 사용자를 분석하고 이메일을 생성할 때 프롬프트에 자동 주입됩니다.
              </Text>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Badge count={activeDirectivesCount} style={{ backgroundColor: '#52c41a' }}>
                <Tag color="green" style={{ fontSize: 12 }}>활성 {activeDirectivesCount}개</Tag>
              </Badge>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginBottom: 20 }}>
            {Object.entries(DIRECTIVE_TYPES).map(([key, cfg]) => (
              <Tooltip key={key} title={cfg.desc}>
                <Button size="small" icon={cfg.icon} onClick={() => openCreateModal(key)}
                  style={{ borderColor: cfg.color, color: cfg.color, fontSize: 12, height: 36, borderRadius: 8 }}>
                  {cfg.label.split(' ')[1]}
                </Button>
              </Tooltip>
            ))}
          </div>

          {directivesLoading ? (
            <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
          ) : directives.length === 0 ? (
            <Empty description={<span>아직 지시사항이 없습니다.<br/>위 버튼을 눌러 첫 지시사항을 추가하세요.</span>} style={{ padding: 60 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openCreateModal()}>지시사항 추가</Button>
            </Empty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {directives.map((d) => {
                const typeConfig = DIRECTIVE_TYPES[d.directive_type] || DIRECTIVE_TYPES.custom;
                return (
                  <div key={d.id} style={{
                    border: `1px solid ${d.is_active ? typeConfig.color + '40' : '#f0f0f0'}`,
                    borderLeft: `4px solid ${d.is_active ? typeConfig.color : '#d9d9d9'}`,
                    borderRadius: 8, padding: '14px 18px',
                    background: d.is_active ? '#fff' : '#fafafa', opacity: d.is_active ? 1 : 0.6, transition: 'all 0.3s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Tag color={typeConfig.color} style={{ borderRadius: 4, fontSize: 11 }}>{typeConfig.label}</Tag>
                      <Text strong style={{ flex: 1, fontSize: 14 }}>{d.title}</Text>
                      {d.target_user_types && d.target_user_types.length > 0 && (
                        <div style={{ display: 'flex', gap: 3 }}>
                          {d.target_user_types.map(t => {
                            const uc = USER_TYPE_CONFIG[t];
                            return uc ? <Tag key={t} style={{ fontSize: 10, borderRadius: 3, background: uc.bg, color: uc.color, border: 'none' }}>{uc.label}</Tag> : null;
                          })}
                        </div>
                      )}
                      {(d.valid_from || d.valid_until) && (
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          <ClockCircleOutlined style={{ marginRight: 3 }} />
                          {d.valid_from ? new Date(d.valid_from).toLocaleDateString('ko-KR') : '~'} → {d.valid_until ? new Date(d.valid_until).toLocaleDateString('ko-KR') : '~'}
                        </Text>
                      )}
                      <Space size={4}>
                        <Tooltip title={d.is_active ? '비활성화' : '활성화'}>
                          <Button size="small" type="text" icon={d.is_active ? <EyeOutlined style={{ color: '#52c41a' }} /> : <EyeInvisibleOutlined style={{ color: '#999' }} />} onClick={() => toggleDirective(d.id)} />
                        </Tooltip>
                        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEditModal(d)} />
                        <Popconfirm title="삭제?" onConfirm={() => deleteDirective(d.id)} okText="삭제" cancelText="취소">
                          <Button size="small" type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                        </Popconfirm>
                      </Space>
                    </div>
                    <div style={{ marginTop: 8, padding: '8px 12px', background: d.is_active ? '#f6f8fa' : '#f0f0f0', borderRadius: 6, fontSize: 13, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {d.content}
                    </div>
                  </div>
                );
              })}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => openCreateModal()} style={{ borderRadius: 8, height: 42, marginTop: 4 }} block>지시사항 추가</Button>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════ */}
      {/* 탭 2: 플랫폼별 에이전트 설정 */}
      {/* ════════════════════════════════════════ */}
      {activeTab === 'settings' && (
        <Card
          title={<Space>{currentConfig.emoji} <span style={{ color: currentConfig.color }}>{currentConfig.label}</span> 에이전트 설정</Space>}
          extra={<Space><Text style={{ fontSize: 12 }}>활성화</Text><Switch checked={isEnabled} onChange={setIsEnabled} /></Space>}
          style={{ borderRadius: 10, borderTop: `3px solid ${currentConfig.color}` }}
        >
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} style={{ width: '100%' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {Object.entries(MODE_INFO).map(([key, info]) => (
                <Radio key={key} value={key} style={{ width: '100%' }}>
                  <div style={{
                    border: mode === key ? `2px solid ${info.color}` : '1px solid #f0f0f0',
                    borderRadius: 10, padding: 14, marginLeft: 4, marginTop: 4, marginBottom: 4,
                    background: mode === key ? `${info.color}08` : '#fff', transition: 'all 0.3s', width: '100%',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 18 }}>{info.icon}</span>
                      <Text strong>{info.title}</Text>
                      {mode === key && <Tag color={info.badge}>선택됨</Tag>}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{info.desc}</Text>
                    {mode === key && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#fafafa', borderRadius: 6, fontSize: 11, color: '#666' }}>{info.detail}</div>
                    )}
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>

          <Divider />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Text>실행 주기:</Text>
            <InputNumber min={1} max={30} value={intervalDays} onChange={setIntervalDays} addonAfter="일" style={{ width: 120 }} />
            <Text type="secondary" style={{ fontSize: 11 }}>매 {intervalDays}일마다 자동 실행</Text>
          </div>

          {activeDirectivesCount > 0 && (
            <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
              <Text style={{ fontSize: 12 }}>현재 <Text strong style={{ color: '#52c41a' }}>{activeDirectivesCount}개</Text>의 마케터 지시사항이 AI 프롬프트에 주입됩니다.</Text>
              <Button type="link" size="small" onClick={() => setActiveTab('directives')} style={{ fontSize: 12, padding: '0 4px' }}>확인하기 →</Button>
            </div>
          )}

          <Button type="primary" block loading={saving} onClick={saveSettings}
            style={{ borderRadius: 8, background: currentConfig.color, borderColor: currentConfig.color }}>
            {currentConfig.emoji} {currentConfig.label} 설정 저장
          </Button>
        </Card>
      )}

      {/* ════════════════════════════════════════ */}
      {/* 탭 3: 실행 이력 (플랫폼 필터) */}
      {/* ════════════════════════════════════════ */}
      {activeTab === 'history' && (
        <Card
          title={<Space><HistoryOutlined /> 실행 이력 — {currentConfig.emoji} {currentConfig.label}</Space>}
          extra={<Text type="secondary" style={{ fontSize: 11 }}>{filteredRuns.length}건 / 전체 {runs.length}건</Text>}
          style={{ borderRadius: 10 }}
        >
          <Table columns={runColumns} dataSource={filteredRuns} rowKey="id" size="small"
            pagination={{ pageSize: 10 }} loading={runsLoading}
            locale={{ emptyText: `${currentConfig.label} 실행 이력이 없습니다` }} />
        </Card>
      )}

      {/* ── 지시사항 모달 ── */}
      <Modal
        title={<Space>{editingDirective ? <EditOutlined /> : <PlusOutlined />}{editingDirective ? '지시사항 수정' : '새 지시사항 추가'}</Space>}
        open={showDirectiveModal} onCancel={() => { setShowDirectiveModal(false); resetDirectiveForm(); }}
        onOk={saveDirective} okText={editingDirective ? '수정' : '추가'} cancelText="취소" width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>지시사항 유형</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {Object.entries(DIRECTIVE_TYPES).map(([key, cfg]) => (
              <div key={key} onClick={() => setDirectiveForm(f => ({ ...f, directive_type: key }))}
                style={{
                  border: directiveForm.directive_type === key ? `2px solid ${cfg.color}` : '1px solid #e8e8e8',
                  borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
                  background: directiveForm.directive_type === key ? `${cfg.color}08` : '#fff', transition: 'all 0.2s', textAlign: 'center',
                }}>
                <div style={{ fontSize: 13, fontWeight: directiveForm.directive_type === key ? 600 : 400, color: cfg.color }}>{cfg.label}</div>
                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{cfg.desc.slice(0, 20)}...</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>제목</Text>
          <Input placeholder="예: 3월 신학기 프로모션 강조" value={directiveForm.title} onChange={e => setDirectiveForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>내용 (AI에 전달될 지시사항)</Text>
          <TextArea rows={4} value={directiveForm.content} onChange={e => setDirectiveForm(f => ({ ...f, content: e.target.value }))}
            placeholder={
              directiveForm.directive_type === 'emphasis' ? '예: 3월 신학기가 시작됩니다. 새 학기를 맞아 REAL의 Write 기능으로 논문 작성을 시작해보세요라는 메시지를 강조하세요.' :
              directiveForm.directive_type === 'tone' ? '예: 친근하고 따뜻한 톤으로 작성하세요.' :
              directiveForm.directive_type === 'exclusion' ? '예: Talk 기능은 현재 개선 중이므로 이번 캠페인에서 언급하지 마세요.' :
              '예: 자유롭게 지시사항을 작성하세요.'
            } />
        </div>
        <Collapse ghost size="small">
          <Panel header={<Text type="secondary" style={{ fontSize: 12 }}>고급 설정</Text>} key="1">
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>대상 사용자 유형 (미선택 시 전체)</Text>
              <Checkbox.Group value={directiveForm.target_user_types || []}
                onChange={v => setDirectiveForm(f => ({ ...f, target_user_types: v.length > 0 ? v : null }))}
                options={Object.entries(USER_TYPE_CONFIG).map(([k, v]) => ({ label: v.label, value: k }))} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>우선순위</Text>
              <InputNumber min={0} max={100} value={directiveForm.priority} onChange={v => setDirectiveForm(f => ({ ...f, priority: v }))} style={{ width: 120 }} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>시작일</Text>
                <Input type="date" value={directiveForm.valid_from ? directiveForm.valid_from.split('T')[0] : ''}
                  onChange={e => setDirectiveForm(f => ({ ...f, valid_from: e.target.value ? e.target.value + 'T00:00:00' : null }))} style={{ width: 160 }} />
              </div>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>종료일</Text>
                <Input type="date" value={directiveForm.valid_until ? directiveForm.valid_until.split('T')[0] : ''}
                  onChange={e => setDirectiveForm(f => ({ ...f, valid_until: e.target.value ? e.target.value + 'T23:59:59' : null }))} style={{ width: 160 }} />
              </div>
            </div>
          </Panel>
        </Collapse>
      </Modal>

      {/* ── 실행 상세 모달 ── */}
      <Modal title="에이전트 실행 상세" open={!!detailModal} onCancel={() => setDetailModal(null)} footer={null} width={700}>
        {detailModal && (
          <div>
            <Space>
              <Tag color={detailModal.run?.status === 'completed' ? 'green' : 'red'}>{detailModal.run?.status}</Tag>
              {detailModal.run?.platform && (
                <Tag color={PLATFORM_CONFIG[detailModal.run.platform]?.color || PLATFORM_CONFIG[(detailModal.run.platform||'').toUpperCase()]?.color}>
                  {PLATFORM_CONFIG[detailModal.run.platform]?.emoji || PLATFORM_CONFIG[(detailModal.run.platform||'').toUpperCase()]?.emoji} {detailModal.run.platform}
                </Tag>
              )}
              <Text>분석 {detailModal.run?.total_analyzed}명</Text>
            </Space>
            {detailModal.run?.summary && (
              <div style={{ background: '#fafafa', borderRadius: 8, padding: 16, margin: '16px 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>{detailModal.run.summary}</div>
            )}
            <Divider>분석된 사용자</Divider>
            <Table size="small" pagination={{ pageSize: 10 }} dataSource={detailModal.insights || []} rowKey="contact_id"
              columns={[
                { title: '사용자', dataIndex: 'email', width: 220, render: (e, r) => e || r.contact_id },
                { title: '이름', dataIndex: 'name', width: 100 },
                { title: '유형', dataIndex: 'user_type', width: 120,
                  render: (t) => { const c = USER_TYPE_CONFIG[t] || USER_TYPE_CONFIG.normal; return <Tag style={{ background: c.bg, color: c.color }}>{c.label}</Tag>; } },
                { title: '점수', dataIndex: 'score', width: 60 },
              ]} />
            {detailModal.actions?.length > 0 && (
              <>
                <Divider>생성된 액션</Divider>
                <Table size="small" dataSource={detailModal.actions} rowKey="contact_id"
                  columns={[
                    { title: '사용자', dataIndex: 'email', width: 220, render: (e, r) => e || r.contact_id },
                    { title: '이름', dataIndex: 'name', width: 100 },
                    { title: '액션', dataIndex: 'type', width: 140 },
                    { title: '상태', dataIndex: 'status', width: 80, render: (s) => <Tag color={s === 'executed' ? 'green' : 'default'}>{s}</Tag> },
                  ]} />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AutoCampaign;
